import type { AuthClientOptions, BffClaims, Brand, CsrfResponse, LoginRedirectOptions, LogoutResponse } from "./types.js";

/** Methods that require a CSRF double-submit token. */
const UNSAFE_METHODS = new Set(["POST", "PUT", "DELETE", "PATCH"]);

/** Cookie name used by the BFF for the double-submit CSRF token. */
const CSRF_COOKIE_NAME = "__Host-mf_csrf";

/** Header the BFF expects the CSRF token to be echoed in. */
const CSRF_HEADER_NAME = "X-CSRF-Token";

/**
 * RFC 4648 §5 base64url, no padding. The adapter's `return` param must be
 * base64url (its `decodeReturnUri` decodes it; Spring Cloud Gateway rejects
 * raw `=`). UTF-8 first so non-ASCII round-trips through java.util.Base64.
 */
function base64UrlEncode(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Framework-agnostic client for the `mf-auth-adapter` BFF.
 *
 * No DOM/React dependencies: usable from any environment with `fetch`,
 * `document.cookie`, and `window.location`. The React surface lives at
 * `@mf/auth-client/react`.
 *
 * All requests are issued with `credentials: "include"` so the host-locked
 * `__Host-mf_session` cookie is sent on same-origin `/api/auth/*` calls.
 */
export class AuthClient {
  private readonly baseUrl: string;

  constructor(opts: AuthClientOptions = {}) {
    const raw = opts.baseUrl ?? "/api/auth";
    this.baseUrl = raw.endsWith("/") ? raw.slice(0, -1) : raw;
  }

  /**
   * Mints (or refreshes) an anonymous session via the HUB_ANONYMOUS policy.
   * Idempotent on the BFF side; safe to call on every page mount.
   *
   * Bootstrap call — does NOT go through {@link fetchWithCsrf}. CSRF
   * double-submit protects existing sessions from cross-site forgery; the
   * anon mint is what *creates* the first session, so there's nothing to
   * protect (and `/csrf` would 401 because no session exists yet — chicken
   * and egg). SameSite=Lax on the resulting `__Host-mf_session` cookie
   * blocks cross-site abuse of the minted session.
   */
  async loginAnonymous(brand: Brand): Promise<BffClaims> {
    const res = await fetch(`${this.baseUrl}/anonymous`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ brand })
    });
    if (!res.ok) {
      throw new AuthClientError(`anonymous login failed: ${res.status}`, res.status);
    }
    return (await res.json()) as BffClaims;
  }

  /**
   * Top-level navigation to the BFF login redirect endpoint.
   * Never returns - the browser unloads. Throws if `window` is unavailable.
   *
   * `opts.return` is the absolute URL to land on after the journey completes.
   * It is base64url-encoded here because the adapter's `decodeReturnUri`
   * base64url-DECODES it (and 400s on anything else), and Spring Cloud Gateway
   * rejects raw/percent-encoded `=` in query values. Callers pass a plain URL.
   */
  loginRedirect(opts: LoginRedirectOptions): never {
    if (typeof window === "undefined") {
      throw new AuthClientError("loginRedirect requires a browser environment");
    }
    const params = new URLSearchParams({ brand: opts.brand });
    if (opts.flow) params.set("flow", opts.flow);
    if (opts.return) params.set("return", base64UrlEncode(opts.return));
    window.location.assign(`${this.baseUrl}/login?${params.toString()}`);
    throw new AuthClientError("navigation did not occur");
  }

  /**
   * POSTs `/logout`, then top-level-navigates to the B2C end-session URL
   * the BFF returns. The session cookie is cleared by the BFF response.
   */
  async logout(): Promise<void> {
    const res = await this.fetchWithCsrf(`${this.baseUrl}/logout`, {
      method: "POST"
    });
    if (!res.ok) {
      throw new AuthClientError(`logout failed: ${res.status}`, res.status);
    }
    const body = (await res.json()) as LogoutResponse;
    if (typeof window !== "undefined" && body.logoutUri) {
      window.location.assign(body.logoutUri);
    }
  }

  /**
   * Returns current claims, or `null` when no session exists (HTTP 401).
   * Any other non-OK status is escalated as `AuthClientError`.
   */
  async me(): Promise<BffClaims | null> {
    const res = await fetch(`${this.baseUrl}/me`, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" }
    });
    if (res.status === 401) return null;
    if (!res.ok) {
      throw new AuthClientError(`me failed: ${res.status}`, res.status);
    }
    return (await res.json()) as BffClaims;
  }

  /**
   * Mints a CSRF token. Side effect: the BFF writes/refreshes the
   * `__Host-mf_csrf` cookie, which `fetchWithCsrf` reads back for double-submit.
   */
  async csrfToken(): Promise<string> {
    const res = await fetch(`${this.baseUrl}/csrf`, {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json" }
    });
    if (!res.ok) {
      throw new AuthClientError(`csrf failed: ${res.status}`, res.status);
    }
    const body = (await res.json()) as CsrfResponse;
    return body.csrfToken;
  }

  /**
   * Drop-in `fetch` wrapper that:
   *   1. forces `credentials: "include"`,
   *   2. on unsafe methods (POST/PUT/DELETE/PATCH) reads the
   *      `__Host-mf_csrf` cookie and adds an `X-CSRF-Token` header,
   *   3. mints a CSRF token via `/csrf` if the cookie is missing.
   */
  async fetchWithCsrf(input: RequestInfo, init: RequestInit = {}): Promise<Response> {
    const method = (init.method ?? "GET").toUpperCase();
    const merged: RequestInit = { ...init, method, credentials: "include" };

    if (UNSAFE_METHODS.has(method)) {
      let token = readCookie(CSRF_COOKIE_NAME);
      if (!token) {
        token = await this.csrfToken();
      }
      const headers = new Headers(init.headers);
      if (!headers.has(CSRF_HEADER_NAME)) {
        headers.set(CSRF_HEADER_NAME, token);
      }
      merged.headers = headers;
    }

    return fetch(input, merged);
  }
}

/** Typed error thrown for non-OK responses from the BFF. */
export class AuthClientError extends Error {
  constructor(
    message: string,
    public readonly status?: number
  ) {
    super(message);
    this.name = "AuthClientError";
  }
}

/**
 * Reads a cookie value from `document.cookie`. Returns `null` when the cookie
 * is absent or no DOM is available (e.g. SSR).
 *
 * Note: __Host-mf_csrf is intentionally NOT HttpOnly so JS can read it for
 * the double-submit pattern. The session cookie is HttpOnly and unreadable.
 */
function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const target = `${name}=`;
  const parts = document.cookie.split(";");
  for (const raw of parts) {
    const trimmed = raw.trim();
    if (trimmed.startsWith(target)) {
      return decodeURIComponent(trimmed.slice(target.length));
    }
  }
  return null;
}
